import { useState, useRef, useCallback, useEffect } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Camera,
  CameraOff,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Trash2,
  Volume2,
  VolumeX,
  ScanBarcode,
  Package,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../utils/api';
import { useLanguage } from '../../../contexts/LanguageContext';
import BatchConfirmationGrid from './BatchConfirmationGrid';

// Dedupe window in milliseconds (same barcode within this time is ignored)
const DEDUPE_WINDOW_MS = 3000;

// Sound for successful scan
const SCAN_BEEP_FREQUENCY = 1800;
const SCAN_BEEP_DURATION = 100;

/**
 * ContinuousScanMode - Phase 2A Component
 * 
 * Camera stays on, detected barcodes accumulate in queue with deduplication.
 * DRLP pans across shelf, each detection queues up with audio/haptic feedback.
 */
export default function ContinuousScanMode({ onComplete, onCancel }) {
  const { t } = useLanguage();
  
  // Refs
  const html5QrCodeRef = useRef(null);
  const scannerContainerRef = useRef(null);
  const audioContextRef = useRef(null);
  const lastScanTimesRef = useRef({});
  
  // State
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [scanQueue, setScanQueue] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [processedResults, setProcessedResults] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Initialize audio context
  useEffect(() => {
    return () => {
      // Cleanup audio context on unmount
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
    };
  }, []);

  // Play scan sound
  const playBeep = useCallback(() => {
    if (!soundEnabled) return;
    
    try {
      // Try Web Audio API first (custom pleasant sound)
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      oscillator.frequency.value = SCAN_BEEP_FREQUENCY;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, ctx.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + SCAN_BEEP_DURATION / 1000);
      
      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + SCAN_BEEP_DURATION / 1000);
      
    } catch (e) {
      // Fallback to browser beep
      try {
        const beep = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2Onp+fnZuYk42HgHp0cm9xcnZ7gYiOlJmdoKKioJ2ZlI+JgoF+fn+Cg4WGh4iJiomJiIeFg4B9e3l4eHl7fYCDhomLjY+QkZGQj42Kh4R/');
        beep.volume = 0.3;
        beep.play();
      } catch (e2) {
        console.log('Audio not supported');
      }
    }
  }, [soundEnabled]);

  // Trigger haptic feedback
  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  }, []);

  // Check if barcode was recently scanned (deduplication)
  const isDuplicate = useCallback((code) => {
    const now = Date.now();
    const lastScan = lastScanTimesRef.current[code];
    
    if (lastScan && (now - lastScan) < DEDUPE_WINDOW_MS) {
      return true;
    }
    
    lastScanTimesRef.current[code] = now;
    return false;
  }, []);

  // Handle barcode detection
  const handleBarcodeDetected = useCallback((decodedText) => {
    // Check for duplicate
    if (isDuplicate(decodedText)) {
      return;
    }
    
    // Add to queue
    setScanQueue(prev => {
      // Check if already in queue
      if (prev.some(item => item.code === decodedText)) {
        return prev;
      }
      
      return [...prev, {
        code: decodedText,
        timestamp: Date.now(),
        status: 'pending'
      }];
    });
    
    // Feedback
    playBeep();
    triggerHaptic();
    
    toast.success(`Scanned: ${decodedText}`, {
      duration: 1500,
      position: 'top-center'
    });
    
  }, [isDuplicate, playBeep, triggerHaptic]);

  // Start continuous camera
  const startCamera = useCallback(async () => {
    try {
      setCameraError(null);
      
      if (!scannerContainerRef.current) return;
      
      html5QrCodeRef.current = new Html5Qrcode('continuous-scanner');
      
      await html5QrCodeRef.current.start(
        { facingMode: 'environment' },
        {
          fps: 15, // Higher FPS for continuous scanning
          qrbox: { width: 280, height: 160 },
          aspectRatio: 1.7778,
          disableFlip: false
        },
        (decodedText) => {
          // Don't stop camera - continuous mode!
          handleBarcodeDetected(decodedText);
        },
        () => {
          // Ignore scan errors (normal during continuous scanning)
        }
      );
      
      setCameraActive(true);
      
    } catch (err) {
      console.error('Camera start error:', err);
      setCameraError('Failed to start camera. Please check permissions.');
      setCameraActive(false);
    }
  }, [handleBarcodeDetected]);

  // Stop camera
  const stopCamera = useCallback(async () => {
    if (html5QrCodeRef.current && cameraActive) {
      try {
        await html5QrCodeRef.current.stop();
      } catch (err) {
        console.error('Camera stop error:', err);
      }
      setCameraActive(false);
    }
  }, [cameraActive]);

  // Clear queue
  const clearQueue = useCallback(() => {
    setScanQueue([]);
    lastScanTimesRef.current = {};
  }, []);

  // Remove single item from queue
  const removeFromQueue = useCallback((code) => {
    setScanQueue(prev => prev.filter(item => item.code !== code));
    delete lastScanTimesRef.current[code];
  }, []);

  // Process queue - lookup all barcodes
  const processQueue = useCallback(async () => {
    if (scanQueue.length === 0) return;
    
    setProcessing(true);
    await stopCamera();
    
    try {
      // Process all barcodes through batch-scan endpoint with scan_mode
      const codes = scanQueue.map(item => item.code);
      const batchResponse = await api.post('/barcode/batch-scan', {
        codes: codes,
        include_lookup: true,
        scan_mode: 'continuous'
      });
      
      if (batchResponse.data.success) {
        const results = batchResponse.data.results.map(result => ({
          raw_code: result.raw_code,
          barcode_type: result.format || 'Unknown',
          parsed: result,
          confidence: result.confidence || 0.5,
          source: result.source || 'barcode',
          ready_to_post: result.product_name !== null,
          needs_input: !result.product_name
        }));
        
        setProcessedResults(results);
        setShowResults(true);
        
        const readyCount = results.filter(r => r.ready_to_post).length;
        toast.success(
          `Processed ${results.length} barcodes: ${readyCount} ready, ${results.length - readyCount} need review`
        );
      } else {
        toast.error('Failed to process barcodes');
      }
      
    } catch (err) {
      console.error('Queue processing error:', err);
      toast.error('Failed to process barcodes');
      
      // Fallback to individual processing
      const results = await Promise.all(
        scanQueue.map(async (item) => {
          try {
            const response = await api.post('/barcode/scan', {
              code: item.code,
              include_lookup: true
            });
            
            if (response.data.success) {
              return {
                raw_code: item.code,
                barcode_type: response.data.result?.format || 'Unknown',
                parsed: response.data.result,
                confidence: response.data.result?.confidence || 0.5,
                source: response.data.result?.source || 'barcode',
                ready_to_post: response.data.result?.product_name !== null,
                needs_input: !response.data.result?.product_name
              };
            } else {
              return {
                raw_code: item.code,
                barcode_type: 'Unknown',
                parsed: null,
                confidence: 0,
                ready_to_post: false,
                needs_input: true,
                error: 'Lookup failed'
              };
            }
          } catch (innerErr) {
            return {
              raw_code: item.code,
              barcode_type: 'Unknown',
              parsed: null,
              confidence: 0,
              ready_to_post: false,
              needs_input: true,
              error: innerErr.message
            };
          }
        })
      );
      
      setProcessedResults(results);
      setShowResults(true);
      
      const readyCount = results.filter(r => r.ready_to_post).length;
      toast.success(
        `Processed ${results.length} barcodes: ${readyCount} ready, ${results.length - readyCount} need review`
      );
    } finally {
      setProcessing(false);
    }
  }, [scanQueue, stopCamera]);

  // Handle batch submit
  const handleBatchSubmit = useCallback(async (items) => {
    if (!items || items.length === 0) return;
    
    setSubmitting(true);
    
    try {
      const response = await api.post('/barcode/batch-create-rshd', { items });
      
      if (response.data.success) {
        const { created, failed } = response.data.summary;
        
        if (created > 0) {
          toast.success(`Successfully posted ${created} products!`);
        }
        
        if (failed > 0) {
          toast.warning(`${failed} products could not be posted`);
        }
        
        if (onComplete) {
          onComplete(response.data);
        }
        
        // Reset
        clearQueue();
        setShowResults(false);
        setProcessedResults([]);
      }
    } catch (err) {
      console.error('Batch submit error:', err);
      toast.error(err.response?.data?.detail || 'Failed to post products');
    } finally {
      setSubmitting(false);
    }
  }, [onComplete, clearQueue]);

  // Reset to scanning mode
  const handleBackToScanning = useCallback(() => {
    setShowResults(false);
    setProcessedResults([]);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current && cameraActive) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, [cameraActive]);

  // Render results view
  if (showResults) {
    return (
      <div className="space-y-4">
        {/* Back button */}
        <Button variant="ghost" onClick={handleBackToScanning} className="mb-2">
          <ScanBarcode className="w-4 h-4 mr-2" />
          {t('scanner_ui.back_to_scanning', 'Back to Scanning')}
        </Button>
        
        {/* Batch confirmation grid */}
        <BatchConfirmationGrid
          items={processedResults}
          onSubmit={handleBatchSubmit}
          onCancel={() => {
            handleBackToScanning();
            clearQueue();
          }}
          loading={submitting}
          title={t('continuous_scan.review_scanned', 'Review Scanned Products')}
          submitLabel={t('continuous_scan.post_selected', 'Post Selected')}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="continuous-scan-mode">
      {/* Camera Card */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Camera className="w-5 h-5 text-green-600" />
                {t('continuous_scan.title', 'Continuous Scan Mode')}
              </CardTitle>
              <CardDescription>
                {t('continuous_scan.description', 'Pan camera across shelf — barcodes will queue automatically')}
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSoundEnabled(!soundEnabled)}
              title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}
            >
              {soundEnabled ? (
                <Volume2 className="w-5 h-5 text-green-600" />
              ) : (
                <VolumeX className="w-5 h-5 text-gray-400" />
              )}
            </Button>
          </div>
        </CardHeader>
        
        <CardContent>
          {cameraError && (
            <Alert variant="destructive" className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{cameraError}</AlertDescription>
            </Alert>
          )}
          
          {/* Camera View */}
          <div className="relative">
            <div
              id="continuous-scanner"
              ref={scannerContainerRef}
              className={`w-full aspect-video bg-gray-900 rounded-lg overflow-hidden ${
                !cameraActive ? 'flex items-center justify-center' : ''
              }`}
              data-testid="continuous-scanner-view"
            >
              {!cameraActive && (
                <div className="text-center text-gray-400">
                  <Camera className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>{t('scanner_ui.camera_inactive', 'Camera inactive')}</p>
                </div>
              )}
            </div>
            
            {/* Scan count badge */}
            {cameraActive && scanQueue.length > 0 && (
              <div className="absolute top-3 right-3">
                <Badge className="bg-green-500 text-white text-lg px-3 py-1 animate-pulse">
                  {scanQueue.length} {t('scanner_ui.scanned', 'scanned')}
                </Badge>
              </div>
            )}
          </div>
          
          {/* Camera Controls */}
          <div className="flex gap-3 mt-4">
            {!cameraActive ? (
              <Button
                onClick={startCamera}
                className="flex-1 bg-green-600 hover:bg-green-700"
                data-testid="start-continuous-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                {t('continuous_scan.start', 'Start Continuous Scan')}
              </Button>
            ) : (
              <Button
                variant="destructive"
                onClick={stopCamera}
                className="flex-1"
                data-testid="stop-continuous-btn"
              >
                <CameraOff className="w-4 h-4 mr-2" />
                {t('continuous_scan.stop', 'Stop Camera')}
              </Button>
            )}
            
            {scanQueue.length > 0 && (
              <Button
                onClick={processQueue}
                disabled={processing}
                className="flex-1"
                data-testid="done-scanning-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('scanner_ui.processing', 'Processing...')}
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    {t('continuous_scan.done', 'Done Scanning')} ({scanQueue.length})
                  </>
                )}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Scan Queue */}
      {scanQueue.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Package className="w-4 h-4 text-blue-600" />
                {t('continuous_scan.queue', 'Scan Queue')}
                <Badge variant="outline">{scanQueue.length}</Badge>
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearQueue}
                className="text-red-500 hover:text-red-700 hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                {t('scanner_ui.clear_all', 'Clear All')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {scanQueue.map((item, index) => (
                  <div
                    key={item.code}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg group"
                    data-testid={`queue-item-${index}`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="font-mono text-xs">
                        #{index + 1}
                      </Badge>
                      <code className="text-sm font-mono">{item.code}</code>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {new Date(item.timestamp).toLocaleTimeString()}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => removeFromQueue(item.code)}
                      >
                        <X className="w-3 h-3 text-gray-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      )}
      
      {/* Instructions */}
      {!cameraActive && scanQueue.length === 0 && (
        <Alert>
          <ScanBarcode className="h-4 w-4" />
          <AlertDescription>
            <strong>{t('continuous_scan.how_to_title', 'How to use:')}</strong>
            <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
              <li>{t('continuous_scan.step1', 'Tap "Start Continuous Scan" to activate camera')}</li>
              <li>{t('continuous_scan.step2', 'Pan slowly across shelf — barcodes auto-detect')}</li>
              <li>{t('continuous_scan.step3', 'Listen for beep on each successful scan')}</li>
              <li>{t('continuous_scan.step4', 'Tap "Done Scanning" to review and post')}</li>
            </ol>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
}
